import { Component, computed, inject, Input, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { LocalHistoryService } from '../../../services/local-history.service';
import { FavoriteGamesService } from '../../../services/games/favorite-games.service';
import { GoogleTagManagerService } from '../../../services/google-tag-manager.service';
import { LanguageService } from '../../../services/language/language.service';
import { BreakpointsDetectorService } from '../../../services/breakpoints-detector.service';
import { DefaultImage, DefaultImageType } from '../../directives/default-image.directive';
import { GameIframeService } from '../../../services/games/game-iframe.service';
import { UserService } from '../../../services/user/user.service';
import { JackpotService } from '../../../../page/jackpot/jackpot.service';
import { EnvironmentService } from '../../../services/environment.service';
import { GamesLauncherService } from '../../../services/games/games-launcher.service';
import { ClassLoadedDirective } from '../../directives/class-loaded.directive';
import { JackpotBadgeComponent } from '../jackpot/jackpot-badge/jackpot-badge.component';
import { SetImgAltDirective } from '../../directives/set-img-alt.directive';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '../../translation/translate.pipe';
import { SafePipe } from '../../pipes/safe.pipe';
import { BADGE_CONFIG } from './badge-config';
import { GamesService } from '../../../services/games/games.service';

@Component({
  selector: 'app-game-preview',
  templateUrl: './game-preview.component.html',
  styleUrls: ['./game-preview.component.scss'],
  imports: [
    SetImgAltDirective,
    NgClass,
    JackpotBadgeComponent,
    ClassLoadedDirective,
    DefaultImage,
    TranslatePipe,
    SafePipe,
    NgTemplateOutlet,
  ],
})
export class GamePreviewComponent implements OnChanges {
  private _router = inject(Router);
  private _localHistory = inject(LocalHistoryService);
  private _gtm = inject(GoogleTagManagerService);
  private _breakPoint = inject(BreakpointsDetectorService);
  private _gameIframe = inject(GameIframeService);
  private _gameLauncher = inject(GamesLauncherService);
  private _favorite = inject(FavoriteGamesService);
  private _games = inject(GamesService);
  user = inject(UserService);
  lang = inject(LanguageService);
  jackpot = inject(JackpotService);
  env = inject(EnvironmentService);

  /**
   * Toggle state for overlay
   */
  public isShowOverlay = computed(() => this._games.clickedGameId() == this.game?.id);

  /**
   * Current game
   */
  @Input() game: any = {};
  readonly isTournamentPreview = input(false);
  readonly isModal = input(false);

  tournamentBadge: string;

  private _badgeConfig = BADGE_CONFIG;

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.game?.firstChange) {
      this._favorite.setFavoriteStatus(this.game || {});
    }
    if (this.isTournamentPreview()) {
      this._getTournamentBadge();
    }
  }

  get defaultImageType() {
    return DefaultImageType;
  }

  /**
   * Redirect to game
   */
  openGame() {
    const gameUrl = `${this.lang.current}/play/${this.game.slug}`;

    this._sendGameClickEvent(!this.user.auth);
    this._localHistory.addUrl('before-play', this._router.url);
    this._localHistory.addUrl('before-login', gameUrl);
    this._router
      .navigateByUrl(gameUrl, {
        state: {
          game: this.game,
        },
      })
      .then();

    if (this.user.auth || (!this.user.auth && !this.game['nodemo'])) {
      this._gameIframe.closeGame();
    }
  }

  onFavoriteClick(game) {
    this._gtm.loginOnHoverGameClick('login_game_hover', 'game_hover');
    this._favorite.toggleFavorite(game);
  }

  /**
   * Toggle overlay for devices
   */
  toggleOverlay() {
    if (this._breakPoint.isTablet || this._breakPoint.isMobile) {
      this._games.clickedGameId.set(this.game.id);
    }
  }

  private _getTournamentBadge() {
    if (!this.game) return '';

    const availableBadges = this._badgeConfig
      .filter(badge => {
        return badge.condition(this.game, this.user, this.jackpot, this.env);
      })
      .sort((a,b) => a.priority - b.priority)
      .map(badge => badge.type)

    if (availableBadges.length === 0) return '';

    this.tournamentBadge = availableBadges[0];
  }

  /**
   * Send game click event
   * @param mode
   * @private
   */
  private _sendGameClickEvent(mode: boolean) {
    const gameIdData = this._gameLauncher.resolveGameId(this.game, mode);
    const gameId = gameIdData && gameIdData.id;

    if (gameId) {
      this._gtm.gameClickEvent(String(gameId));
    }
  }
}
